(function () {
  /**
   * Delivery section of the checkout sheet — saved-address picker (default
   * pre-selected) or new-address form, reusing the same Iraq catalog +
   * searchable area picker as the account address form. Mirrors
   * checkout_delivery_section.dart / checkout_saved_address_editor.dart.
   */

  var STR = (window.ErosAccountStrings && window.ErosAccountStrings.checkout) || {};
  function t(key, fallback) {
    return STR[key] || fallback;
  }
  var LOCALE = (document.documentElement.getAttribute('lang') || 'ar').split('-')[0];

  // GET /v1/customer/addresses returns raw Shopify fields, not the
  // governorateId/areaId sent on write — decode zip ("{governorateId}-{areaId}")
  // back to resolve an area-only label for the dropdown, matching
  // checkout_saved_address_picker.dart's areaLabelFor(). Same decode as
  // eros-account-addresses.js's decodeZip.
  /**
   * The Waseet `governorate-area` code. It lives in `company`; it used to be
   * in `zip`, but Iraq has no postal-code format matching `5-5433` and
   * Shopify rejected every saved address (owner 2026-09-09). Shopify has no
   * metafields on an address, so `company` — unused by Eros otherwise —
   * holds it. Reads still fall back to `zip` for rows saved before the move.
   */
  function waseetCodeOf(addr) {
    if (!addr) return '';
    return (addr.company || addr.zip || '').trim();
  }

  // The new-address form opens with the signed-in phone, as the app's
  // address form does (defaultPhone); the customer changes it when the
  // parcel goes to someone else (owner 2026-09-25). Local format, the way
  // the account form shows it.
  function sessionLocalPhone() {
    var session = window.ErosSession && window.ErosSession.get ? window.ErosSession.get() : null;
    var e164 = session && session.e164 ? String(session.e164).trim() : '';
    if (e164.indexOf('+964') === 0) return '0' + e164.slice(4);
    if (e164.indexOf('964') === 0 && e164.length > 3) return '0' + e164.slice(3);
    return e164;
  }

  function decodeZip(zip) {
    if (!zip) return null;
    var parts = String(zip).split('-');
    if (parts.length !== 2) return null;
    return { governorateId: parts[0], areaId: parts[1] };
  }

  /**
   * ADR-183: the governorate + area a saved address resolves to in the
   * current catalog, or nulls. An address that does not resolve — no code
   * (699 legacy rows on 2026-09-25) or a code the catalog no longer has —
   * "needs updating": it cannot confirm an order, because the courier
   * upload is code-only (ADR-180) and such an order would wait for manual
   * handling. Mirrors ErosDeliveryAddressCodec.needsAreaUpdate (Flutter).
   */
  function resolveSavedPlace(addr, catalog) {
    var decoded = decodeZip(waseetCodeOf(addr));
    var gov = decoded && catalog ? window.ErosIraqAddressCatalog.governorateById(catalog, decoded.governorateId) : null;
    var area = gov ? window.ErosIraqAddressCatalog.areaById(gov, decoded.areaId) : null;
    return { gov: gov, area: area };
  }

  function savedAddressNeedsUpdate(addr, catalog) {
    var place = resolveSavedPlace(addr, catalog);
    return !(place.gov && place.area);
  }

  function areaLabelForAddress(addr, catalog) {
    var place = resolveSavedPlace(addr, catalog);
    if (place.area) return window.ErosIraqAddressCatalog.areaLabelForLocale(place.area, LOCALE);
    var stored = addr.address1 || addr.city || addr.id;
    // Marked in the dropdown itself, the way the app badges the row.
    return stored + ' — ' + t('needs_update', 'يحتاج تحديث');
  }

  /**
   * @param {HTMLElement} container
   * @param {function()} onChange called whenever the resolved address state changes
   * @returns {{ getState: function(): {savedAddressGid: ?string, address: ?object, valid: boolean} }}
   */
  function mount(container, onChange) {
    var state = {
      mode: 'loading', // 'saved' | 'new' | 'loading'
      addresses: [],
      selectedSavedId: null,
      newAddress: { phone: sessionLocalPhone(), governorateId: null, areaId: null, landmark: '' },
      catalog: null,
    };

    container.innerHTML = '';
    var loading = window.ErosUI.el('p');
    loading.textContent = t('loading', 'جارٍ التحميل...');
    container.appendChild(loading);

    Promise.all([window.ErosCommerce.listAddresses(), window.ErosIraqAddressCatalog.get()])
      .then(function (results) {
        state.addresses = results[0].addresses || [];
        state.catalog = results[1];
        var def = state.addresses.filter(function (a) { return a.isDefault; })[0] || state.addresses[0];
        if (def) {
          state.mode = 'saved';
          state.selectedSavedId = def.id;
        } else {
          state.mode = 'new';
        }
        render();
      })
      .catch(function () {
        container.innerHTML = '';
        var err = window.ErosUI.el('p', 'eros-field__error');
        err.textContent = t('load_error', 'تعذّر تحميل بيانات التوصيل');
        container.appendChild(err);
      });

    function render() {
      container.innerHTML = '';

      if (state.addresses.length) {
        var row = window.ErosUI.el('div', 'eros-checkout-address-row');
        var select = window.ErosUI.el('select', 'select');
        state.addresses.forEach(function (addr) {
          var o = document.createElement('option');
          o.value = addr.id;
          var label = areaLabelForAddress(addr, state.catalog);
          o.textContent = label + (addr.isDefault ? ' (' + t('default_badge', 'افتراضي') + ')' : '');
          select.appendChild(o);
        });
        var manualOpt = document.createElement('option');
        manualOpt.value = '__manual__';
        manualOpt.textContent = t('new_address', 'إضافة عنوان آخر');
        select.appendChild(manualOpt);
        select.value = state.mode === 'new' ? '__manual__' : state.selectedSavedId;

        // Edit only makes sense for an actual saved selection — matches
        // checkout_saved_address_picker.dart (hidden for the manual-entry state).
        var editBtn = window.ErosUI.el('button', 'link', { type: 'button' });
        editBtn.textContent = t('edit_address', 'تعديل العنوان');
        editBtn.style.display = state.mode === 'saved' ? '' : 'none';
        editBtn.addEventListener('click', function () {
          openEditor(state.selectedSavedId);
        });

        select.addEventListener('change', function () {
          if (select.value === '__manual__') {
            state.mode = 'new';
          } else {
            state.mode = 'saved';
            state.selectedSavedId = select.value;
          }
          onChange();
          render();
        });

        row.appendChild(select);
        row.appendChild(editBtn);
        container.appendChild(row);

        // ADR-183: say it before the confirm button does. The default row is
        // preselected, so a legacy address would otherwise sit there looking
        // ready until the tap.
        if (state.mode === 'saved') {
          var selected = state.addresses.filter(function (a) { return a.id === state.selectedSavedId; })[0];
          if (selected && savedAddressNeedsUpdate(selected, state.catalog)) {
            var hint = window.ErosUI.el('p', 'eros-field__error');
            hint.textContent = t('address_needs_update', 'تعذّر تحديد هذا العنوان. حدّث المحافظة والمنطقة ثم أعد المحاولة.');
            container.appendChild(hint);
          }
        }
      }

      if (state.mode === 'new') {
        container.appendChild(buildNewAddressForm());
      }
    }

    function buildNewAddressForm() {
      var form = window.ErosUI.el('div', 'eros-checkout-new-address');

      var phoneField = window.ErosUI.el('div', 'form-control');
      var phoneLabel = window.ErosUI.el('label');
      phoneLabel.textContent = t('phone_label', 'رقم الهاتف');
      var phoneInput = window.ErosUI.el('input', 'input', { type: 'tel' });
      phoneInput.value = state.newAddress.phone;
      phoneInput.addEventListener('input', function () {
        state.newAddress.phone = phoneInput.value;
        onChange();
      });
      phoneField.appendChild(phoneLabel);
      phoneField.appendChild(phoneInput);

      var govField = window.ErosUI.el('div', 'form-control');
      var govLabel = window.ErosUI.el('label');
      govLabel.textContent = t('governorate_label', 'المحافظة');
      var govSelect = window.ErosUI.el('select', 'select');
      // No pre-selected governorate (owner 2026-10-01) — it used to open on
      // the first one (بغداد); the customer picks it, then the area.
      var govPlaceholder = document.createElement('option');
      govPlaceholder.value = '';
      govPlaceholder.disabled = true;
      govPlaceholder.textContent = t('governorate_select_trigger', 'اختر المحافظة');
      govSelect.appendChild(govPlaceholder);
      state.catalog.governorates.forEach(function (gov) {
        var o = document.createElement('option');
        o.value = gov.id;
        o.textContent = window.ErosIraqAddressCatalog.governorateNameForLocale(gov, LOCALE);
        govSelect.appendChild(o);
      });
      govSelect.value = state.newAddress.governorateId || '';
      govField.appendChild(govLabel);
      govField.appendChild(govSelect);

      var areaField = window.ErosUI.el('div', 'form-control');
      var areaLabel = window.ErosUI.el('label');
      areaLabel.textContent = t('area_label', 'المنطقة');
      var areaTrigger = window.ErosUI.el('button', 'input eros-area-trigger', { type: 'button' });
      areaField.appendChild(areaLabel);
      areaField.appendChild(areaTrigger);

      function refreshAreaTrigger() {
        var gov = window.ErosIraqAddressCatalog.governorateById(state.catalog, state.newAddress.governorateId);
        var area = gov ? window.ErosIraqAddressCatalog.areaById(gov, state.newAddress.areaId) : null;
        areaTrigger.textContent = area
          ? window.ErosIraqAddressCatalog.areaLabelForLocale(area, LOCALE)
          : t('area_select_trigger', 'اختر المنطقة');
      }
      refreshAreaTrigger();

      govSelect.addEventListener('change', function () {
        state.newAddress.governorateId = govSelect.value;
        state.newAddress.areaId = null;
        refreshAreaTrigger();
        onChange();
      });

      areaTrigger.addEventListener('click', function () {
        var gov = window.ErosIraqAddressCatalog.governorateById(state.catalog, govSelect.value);
        if (!gov) return;
        window.ErosIraqAreaPicker.open(gov, LOCALE, state.newAddress.areaId, function (area) {
          state.newAddress.areaId = area.id;
          refreshAreaTrigger();
          onChange();
        });
      });

      var landmarkField = window.ErosUI.el('div', 'form-control');
      var landmarkLabel = window.ErosUI.el('label');
      landmarkLabel.textContent = t('landmark_label', 'أقرب نقطة دالة');
      var landmarkInput = window.ErosUI.el('input', 'input', { type: 'text' });
      landmarkInput.value = state.newAddress.landmark;
      landmarkInput.addEventListener('input', function () {
        state.newAddress.landmark = landmarkInput.value;
        if (landmarkInput.value.trim()) window.ErosUI.setFieldError(landmarkInput, null);
        onChange();
      });
      state.landmarkInput = landmarkInput;
      landmarkField.appendChild(landmarkLabel);
      landmarkField.appendChild(landmarkInput);

      form.appendChild(phoneField);
      form.appendChild(govField);
      form.appendChild(areaField);
      form.appendChild(landmarkField);
      return form;
    }

    // Opens the account address form on one saved row (the checkout's own
    // "Edit address" button, and the ADR-183 refusal path from eros-checkout.js
    // when the worker answers address_needs_update). Reloads the list on save.
    function openEditor(addressId) {
      var addr = state.addresses.filter(function (a) { return a.id === addressId; })[0];
      if (!addr || !window.ErosAccountAddresses) return false;
      window.ErosAccountAddresses.openEditDialog(addr, t('edit_address', 'تعديل العنوان'), function () {
        window.ErosCommerce.listAddresses().then(function (res) {
          state.addresses = res.addresses || [];
          render();
          onChange();
        });
      });
      return true;
    }

    function sameAddressId(a, b) {
      // The worker echoes a normalised gid; rows may carry the ?model_name form.
      var na = String(a || '').split('?')[0];
      var nb = String(b || '').split('?')[0];
      return !!na && na === nb;
    }

    return {
      // ADR-215 §2.3: what eros-checkout-address-check.js reads — already loaded, no request.
      getSavedAddresses: function () { return state.addresses; },
      getCatalog: function () { return state.catalog; },
      openEditorForSelected: function () {
        return state.mode === 'saved' ? openEditor(state.selectedSavedId) : false;
      },
      openEditorFor: function (savedAddressGid) {
        var match = state.addresses.filter(function (a) { return sameAddressId(a.id, savedAddressGid); })[0];
        return openEditor(match ? match.id : state.selectedSavedId);
      },
      getState: function () {
        if (state.mode === 'saved' && state.selectedSavedId) {
          var addr = state.addresses.filter(function (a) { return a.id === state.selectedSavedId; })[0];
          if (!addr) return { savedAddressGid: null, address: null, valid: false };
          // ADR-183: an address the courier cannot receive never confirms.
          if (savedAddressNeedsUpdate(addr, state.catalog)) {
            return { savedAddressGid: state.selectedSavedId, address: null, valid: false, reason: 'address_needs_update' };
          }
          // commerce-worker requires a full `address` object unconditionally
          // (checkout.ts: `if (!body.address) return address_required`) —
          // savedAddressGid alone isn't enough, it's only a hint to reuse the
          // exact existing Shopify mailing address row rather than create a
          // new one. Must still send the saved address's own fields.
          return {
            savedAddressGid: state.selectedSavedId,
            address: {
              firstName: addr.firstName || undefined,
              lastName: addr.lastName || undefined,
              phone: addr.phone,
              address1: addr.address1,
              address2: addr.address2 || undefined,
              city: addr.city,
              province: addr.province || undefined,
              company: waseetCodeOf(addr) || undefined,
              countryCode: 'IQ',
            },
            valid: true,
          };
        }
        if (state.mode === 'new') {
          var gov = state.catalog ? window.ErosIraqAddressCatalog.governorateById(state.catalog, state.newAddress.governorateId) : null;
          var area = gov ? window.ErosIraqAddressCatalog.areaById(gov, state.newAddress.areaId) : null;
          // ADR-215 §2.3: the landmark is required, as the account editor requires it
          // (an area alone does not get a courier to the door).
          var landmarkOk = !!state.newAddress.landmark.trim();
          if (!landmarkOk && state.landmarkInput) {
            var checkStr = (window.ErosAccountStrings && window.ErosAccountStrings.address_check) || {};
            window.ErosUI.setFieldError(state.landmarkInput, checkStr.required || 'مطلوب');
          }
          var valid = !!(state.newAddress.phone.trim() && gov && area && landmarkOk);
          if (!valid) return { savedAddressGid: null, address: null, valid: false };
          return {
            savedAddressGid: null,
            address: {
              phone: state.newAddress.phone.trim(),
              address1: window.ErosIraqAddressCatalog.areaLabelForLocale(area, 'ar'),
              address2: state.newAddress.landmark.trim() || undefined,
              city: window.ErosIraqAddressCatalog.governorateNameForLocale(gov, 'ar'),
              // The Waseet code (`govId-areaId`) rides on `company`, exactly as
              // the saved-address path above and Flutter's
              // native_delivery_address.dart send it — the courier upload
              // no longer has to re-match the area by its label text.
              company: gov.id + '-' + area.id,
              countryCode: 'IQ',
            },
            valid: true,
          };
        }
        return { savedAddressGid: null, address: null, valid: false };
      },
    };
  }

  window.ErosCheckoutDelivery = { mount: mount };
})();
